import {
  useAudiences,
  useCategories,
  useDeviceTypes,
  useGenres,
  useProviders,
  useRatings,
  useShowTypes,
} from '@/api/queries';
import Vocabulary, { VocabularyColumn } from './Vocabulary';

/**
 * The vocabulary screens, declared rather than written out.
 *
 * Each is the same table over a different entity, so what actually differs — the columns and the
 * mutation names — is all that is stated here. Adding the next vocabulary is an entry in this file
 * plus a route, not a new page.
 */
const NAMED: VocabularyColumn[] = [
  {
    field: 'id',
    label: 'ID',
    required: true,
    helper: 'The value as it appears in the ADI XML.',
  },
  {
    field: 'name',
    label: 'Name',
    required: true,
    helper: 'What operators see in the editor.',
  },
];

export const RatingsPage = () => {
  const { data, isLoading, error } = useRatings();
  return (
    <Vocabulary
      title="Ratings"
      description="Age and content ratings an item may carry. Items whose rating is not on this list fail the rating-vocabulary rule."
      queryName="ratings"
      setMutation="setRating"
      deleteMutation="deleteRating"
      columns={[
        ...NAMED,
        {
          field: 'order',
          label: 'Order',
          type: 'number',
          helper: 'Sort order in the editor.',
        },
      ]}
      entries={data as unknown as Record<string, unknown>[]}
      isLoading={isLoading}
      error={error}
    />
  );
};

export const GenresPage = () => {
  const { data, isLoading, error } = useGenres();
  return (
    <Vocabulary
      title="Genres"
      description="Genres an item may be tagged with. Real provider packages frequently carry genres that are not on this list — those show as validation errors rather than being silently accepted."
      queryName="genres"
      setMutation="setGenre"
      deleteMutation="deleteGenre"
      columns={NAMED}
      entries={data as unknown as Record<string, unknown>[]}
      isLoading={isLoading}
      error={error}
    />
  );
};

export const ShowTypesPage = () => {
  const { data, isLoading, error } = useShowTypes();
  return (
    <Vocabulary
      title="Show types"
      description="Movie, Series and anything else a provider sends. The series flag is what makes the season and episode rules apply to an item."
      queryName="showTypes"
      setMutation="setShowType"
      deleteMutation="deleteShowType"
      columns={[
        ...NAMED,
        {
          field: 'isSeries',
          label: 'Is series',
          type: 'boolean',
          helper:
            'Enables the season/episode validation rules for items of this type.',
        },
      ]}
      entries={data as unknown as Record<string, unknown>[]}
      isLoading={isLoading}
      error={error}
    />
  );
};

export const AudiencesPage = () => {
  const { data, isLoading, error } = useAudiences();
  return (
    <Vocabulary
      title="Audiences"
      description="Target audience values, as carried in the ADI App_Data."
      queryName="audiences"
      setMutation="setAudience"
      deleteMutation="deleteAudience"
      columns={NAMED}
      entries={data as unknown as Record<string, unknown>[]}
      isLoading={isLoading}
      error={error}
    />
  );
};

export const DeviceTypesPage = () => {
  const { data, isLoading, error } = useDeviceTypes();
  return (
    <Vocabulary
      title="Device types"
      description="Device types an item can be restricted to or from."
      queryName="deviceTypes"
      setMutation="setDeviceType"
      deleteMutation="deleteDeviceType"
      columns={NAMED}
      entries={data as unknown as Record<string, unknown>[]}
      isLoading={isLoading}
      error={error}
    />
  );
};

export const ProvidersPage = () => {
  const { data, isLoading, error } = useProviders();
  return (
    <Vocabulary
      title="Providers"
      description="Content providers, and the asset-id prefix each one uses. The prefix is how an incoming package is attributed."
      queryName="providers"
      setMutation="setProvider"
      deleteMutation="deleteProvider"
      columns={[
        ...NAMED,
        { field: 'prefix', label: 'Asset ID prefix', required: true },
        { field: 'providerId', label: 'Provider ID' },
      ]}
      entries={data as unknown as Record<string, unknown>[]}
      isLoading={isLoading}
      error={error}
    />
  );
};

export const CategoriesPage = () => {
  const { data, isLoading, error } = useCategories();
  return (
    <Vocabulary
      title="Categories"
      description="The category tree, as a flat list of paths."
      queryName="categories"
      setMutation=""
      deleteMutation=""
      idField="path"
      columns={[
        { field: 'path', label: 'Path' },
        { field: 'name', label: 'Name' },
      ]}
      entries={data as unknown as Record<string, unknown>[]}
      isLoading={isLoading}
      error={error}
      readOnly
      readOnlyReason="Categories are derived from the paths present on ingested items, not configured separately — so this list reflects the catalogue rather than driving it. A managed tree with its own CRUD is roadmap item 3.7."
    />
  );
};
